import { useEffect, useState } from "react";
import axios from "axios";
import { Heart, TrendingUp, CheckCircle, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BriefImpactPanel — small monthly stats panel for the ADI dashboard.
 * Shows briefs viewed, helpful %, check-in coverage, top support cues,
 * and a subtle positive trend when usage is climbing.
 *
 * Kept compact and motivating — this is a feedback loop, not a report.
 */
export default function BriefImpactPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/work-mode/mindset-brief/impact`, { withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data) return null;

  // First-time empty state — motivational nudge, not a shame.
  if (data.briefs_viewed === 0) {
    return (
      <div
        data-testid="brief-impact-empty"
        className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-4 flex items-start gap-3"
      >
        <div className="w-9 h-9 rounded-xl bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
          <Heart className="w-4 h-4 text-slate-500" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] font-extrabold tracking-wider text-slate-500 uppercase">Mindset Brief · Impact</p>
          <p className="text-sm font-bold text-slate-700 dark:text-slate-200 mt-0.5 leading-snug">{data.headline}</p>
          <p className="text-xs text-slate-500 mt-1">Your next pupil's check-in will show up here 30 min before their lesson.</p>
        </div>
      </div>
    );
  }

  const trendLabel = data.trend === "up" ? "Up on last week" : null;

  return (
    <div
      data-testid="brief-impact-panel"
      className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-3"
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-rose-100 dark:bg-rose-900/20 flex items-center justify-center flex-shrink-0">
          <Heart className="w-4 h-4 text-rose-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[10px] font-extrabold tracking-wider text-slate-500 uppercase">Mindset Brief · this month</p>
            {trendLabel && (
              <span data-testid="brief-impact-trend" className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                <TrendingUp className="w-2.5 h-2.5" />{trendLabel}
              </span>
            )}
          </div>
          <p data-testid="brief-impact-headline" className="text-sm font-extrabold text-slate-900 dark:text-white mt-0.5 leading-snug">
            {data.headline}
          </p>
          {data.lessons > 0 && (
            <p data-testid="brief-impact-checkin-line" className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
              Learners checked in before {data.checkin_pct}% of lessons.
            </p>
          )}
        </div>
      </div>

      {/* 3-stat compact row */}
      <div className="grid grid-cols-3 gap-2">
        <Stat testId="stat-viewed" label="Viewed" value={data.briefs_viewed} />
        <Stat testId="stat-helpful" label="Helpful" value={`${data.helpful_pct}%`} />
        <Stat testId="stat-checkins" label="Check-ins" value={`${data.checkin_pct}%`} hint={data.lessons ? `${data.lessons_with_checkin}/${data.lessons}` : null} />
      </div>

      {/* Top cues */}
      {data.top_cues?.length > 0 && (
        <div data-testid="brief-impact-cues" className="pt-2 border-t border-slate-100 dark:border-slate-700">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Most common today-pattern</p>
          <ul className="space-y-1">
            {data.top_cues.map((c, i) => (
              <li key={i} data-testid={`brief-impact-cue-${i}`} className="flex items-center justify-between text-xs text-slate-700 dark:text-slate-200">
                <span className="flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-amber-500" />
                  {c.label}
                </span>
                <span className="text-[10px] text-slate-500 tabular-nums">{c.count}×</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Gentle close: praise, not metric */}
      {data.helpful_pct >= 70 && (
        <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 dark:text-emerald-400">
          <CheckCircle className="w-3.5 h-3.5" />
          <span>Your pupils are quietly better for these 30 seconds.</span>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hint, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl bg-slate-50 dark:bg-slate-900/40 p-2.5 text-center">
      <p className="text-lg font-extrabold text-slate-900 dark:text-white leading-none tabular-nums">{value}</p>
      <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">{label}</p>
      {hint && <p className="text-[9px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}
